import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { EventItem } from '../../types/event';
import { getEventById } from '../../services/eventService';
import { checkUserEventApplication } from '../../services/applicationService';
import EventDetailCard from '../common/EventDetailCard';
import PageHeader from '../common/PageHeader';
import { logger } from '../../utils/logger';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCheckCircle, faFileAlt } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../context/AuthContext';
import { useAnalytics, useScrollTracking } from '../../hooks/useAnalytics';
import '../../styles/EventDetailPage.css';

const EventDetailPage: React.FC = () => {
  const { trackPageView, trackCustomEvent } = useAnalytics();
  useScrollTracking(); // Automatic scroll tracking

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [event, setEvent] = useState<EventItem | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [hasApplied, setHasApplied] = useState<boolean>(false);
  const [applicationId, setApplicationId] = useState<string | undefined>(undefined);
  const [applicationStatus, setApplicationStatus] = useState<string | undefined>(undefined);
  const [checkingApplication, setCheckingApplication] = useState<boolean>(false);

  useEffect(() => {
    const fetchEventDetails = async () => {
      if (!id) {
        setError('Event ID is missing');
        setLoading(false);
        return;
      }

      try {
        logger.info('🔄 Loading event details', { component: 'EventDetailPage', eventId: id });
        const eventData = await getEventById(id);

        if (eventData) {
          setEvent(eventData);
          logger.success('✅ Event details loaded successfully', { 
            component: 'EventDetailPage',
            eventId: id,
            title: eventData.title
          });
          
          // Track page view with proper parameters
          trackPageView({ 
            page_title: `Event Detail: ${eventData.title}`,
            page_location: window.location.href
          });
          
          // Track event detail view
          trackCustomEvent('event_detail_viewed', {
            event_id: id,
            event_title: eventData.title,
            event_date: eventData.date,
            page: 'event_detail'
          });
        } else {
          setError('Event not found');
          logger.error('❌ Event not found', { component: 'EventDetailPage', eventId: id });
        }
      } catch (error) {
        logger.error('❌ Failed to load event details:', error);
        setError('Unable to load event details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchEventDetails();
  }, [id, trackPageView, trackCustomEvent]);

  // Check if the user has already applied to this event
  useEffect(() => {
    if (!id || !currentUser) return;
    
    const checkApplicationStatus = async () => {
      setCheckingApplication(true);
      try {
        logger.info('🔍 Checking if user has already applied', { eventId: id });
        const result = await checkUserEventApplication(id);
        
        setHasApplied(result.hasApplied);
        setApplicationId(result.applicationId);
        
        // Only set the application status if the user has actually applied
        // This ensures withdrawn applications don't show a status
        if (result.hasApplied) {
          setApplicationStatus(result.status);
        } else {
          setApplicationStatus(undefined);
        }
        
        logger.info('✅ Application status check completed', { 
          hasApplied: result.hasApplied,
          applicationId: result.applicationId,
          status: result.status
        });
      } catch (error) {
        logger.error('❌ Error checking application status:', error);
        // Don't set an error state here to avoid disrupting the UI
        // Just assume user hasn't applied yet
        setHasApplied(false);
        setApplicationStatus(undefined);
      } finally {
        setCheckingApplication(false);
      }
    };
    
    checkApplicationStatus();
  }, [id, currentUser]);

  const handleApply = () => {
    // Track apply button click
    trackCustomEvent('event_apply_clicked', {
      event_id: id || '',
      event_title: event?.title || '',
      user_authenticated: !!currentUser,
      page: 'event_detail'
    });

    // If user is not logged in, redirect to login page with return URL
    if (!currentUser) {
      logger.info('👤 User not authenticated, redirecting to login first');
      
      // Track unauthenticated apply attempt
      trackCustomEvent('apply_unauthenticated_redirect', {
        event_id: id || '',
        page: 'event_detail'
      });
      
      // Save the current location to redirect back after login
      const returnUrl = `/events/${id}`;
      navigate(`/sign-in?returnUrl=${encodeURIComponent(returnUrl)}`);
      return;
    }
    
    // Check if the user has already applied to this event
    if (hasApplied && applicationId) {
      // If they've already applied, show them their application status instead
      logger.info('👤 User has already applied, redirecting to application status', { 
        applicationId, 
        status: applicationStatus 
      });
      navigate(`/eventapplication/${applicationId}`);
      return;
    }
    
    // If they haven't applied yet, proceed to application page
    navigate(`/apply?eventId=${id}`);
  };
  
  const handleViewStatus = () => {
    // Track view status click
    trackCustomEvent('application_status_viewed', {
      event_id: id || '',
      application_id: applicationId || '',
      application_status: applicationStatus || '',
      page: 'event_detail'
    });

    if (applicationId) {
      navigate(`/eventapplication/${applicationId}`);
    }
  };

  // Format event date for meta title
  const formatEventDate = (dateString?: string): string => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Determine if event is in the past
  const isEventPast = (): boolean => {
    if (!event?.date) return false;
    const eventDate = new Date(event.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return eventDate < today;
  };

  return (
    <>
      <PageHeader
        title={event ? event.title : 'Event Details'}
        subtitle={event ? formatEventDate(event.date) : 'Loading event information'}
      />

      <section className="container edp-event-container">
        <div className="edp-navigation">
          <Link to="/events" className="edp-back-link">
            <FontAwesomeIcon icon={faArrowLeft} /> Back to all events
          </Link>
        </div>

        {!loading && !error && event && (
          <>
            <div className="edp-apply-container">
              {/* Show different buttons based on application status */}
              {hasApplied ? (
                <>
                  <div className="edp-already-applied">
                    <button
                      onClick={handleViewStatus}
                      className={`edp-status-button ${
                        applicationStatus === 'rejected' ? 'rejected' : 
                        applicationStatus === 'pending' ? 'pending' : 
                        applicationStatus === 'approved' ? 'approved' : ''
                      }`}
                    >
                      <FontAwesomeIcon icon={faFileAlt} />
                      {applicationStatus === 'withdrawn' 
                        ? 'View Withdrawn Application' 
                        : applicationStatus === 'approved'
                          ? 'Already Applied (Approved)' 
                          : applicationStatus === 'pending'
                            ? 'Already Applied (Pending)' 
                            : applicationStatus === 'rejected'
                              ? 'Already Applied (Rejected)'
                              : `Already Applied (${applicationStatus || 'Unknown'})`
                      }
                    </button>
                    <p className={`edp-status-note ${
                      applicationStatus === 'rejected' ? 'rejected' : 
                      applicationStatus === 'pending' ? 'pending' : 
                      applicationStatus === 'approved' ? 'approved' : ''
                    }`}>
                      You have already applied for this event. Click above to check your application status.
                    </p>
                  </div>
                </>
              ) : (
                <button
                  onClick={handleApply}
                  className={`edp-apply-button ${isEventPast() ? 'past' : ''}`}
                  disabled={isEventPast() || checkingApplication}
                >
                  <FontAwesomeIcon icon={faCheckCircle} />
                  {isEventPast() ? 'Event has ended' : checkingApplication ? 'Loading...' : 'Apply for this event'}
                </button>
              )}
              
              {isEventPast() && (
                <p className="edp-past-notice">
                  This event has already taken place. Check our upcoming events!
                </p>
              )}
            </div>

            <EventDetailCard event={event} />
          </>
        )}

        {loading && (
          <div className="edp-loading">
            <p>Loading event details...</p>
          </div>
        )}

        {error && (
          <div className="edp-error">
            <p>{error}</p>
            <Link to="/events" className="edp-back-link-centered">
              View all events
            </Link>
          </div>
        )}
      </section>
    </>
  );
};

export default EventDetailPage;